import { z } from 'zod';
import { idSchema, isoDateTimeSchema, queryNumberSchema } from './common.schema';
import { ALERT_SEVERITIES, ALERT_STATUSES, ALERT_RESOLVE_REASONS } from '../constants/severities';
import { SDT_SCOPE_TYPES } from './alerting.schema';
/**
 * Alert investigation (RCA) read API — issue #21. A tenant-scoped,
 * READ-ONLY surface under `/alerts/:id/{context,related,metrics}` for
 * anyone reasoning about a firing alert (a `det_` token holder, an AIOps
 * tool, a Gendesk persona, a human). It adds no write path into alert
 * state: ack ≠ resolve, metric truth is untouched.
 *
 * Query params arrive as strings; the schemas coerce and bound them and
 * are strict, so an unknown or repeated param is a 400, never ignored.
 */
export const INVESTIGATION_LIMITS = {
    /** Minutes before / after the alert's startedAt covered by a window. */
    windowMinutesMax: 1440,
    windowBeforeDefault: 60,
    windowAfterDefault: 60,
    relatedDefault: 25,
    relatedMax: 50,
    /** Neighbour resources considered for related-alert correlation. */
    neighboursMax: 29,
    /** Alerts read per resource per query (each live status, and resolved-
     *  since-window-start); more than this is reported as truncated. */
    alertScanLimit: 100,
    /** Resources per page / page cap when collecting scoped group members. */
    neighbourScanPageSize: 100,
    neighbourScanMaxPages: 5,
    /** Series returned by /metrics (the alert's own + sibling datapoints). */
    seriesMax: 10,
    changesLookbackDefaultHours: 24,
    changesLookbackMaxHours: 168,
    changesPerEntityMax: 25,
    /** Serialized size cap per before/after value in a change entry. */
    changeValueMaxChars: 1000,
};
const boundedInt = (min, max, def) => queryNumberSchema
    .pipe(z.number().int().min(min).max(max))
    .optional()
    .transform((v) => v ?? def);
const windowShape = {
    beforeMinutes: boundedInt(0, INVESTIGATION_LIMITS.windowMinutesMax, INVESTIGATION_LIMITS.windowBeforeDefault),
    afterMinutes: boundedInt(0, INVESTIGATION_LIMITS.windowMinutesMax, INVESTIGATION_LIMITS.windowAfterDefault),
};
/** GET /alerts/:id/context */
export const investigationContextQuerySchema = z
    .object({
    changesLookbackHours: boundedInt(1, INVESTIGATION_LIMITS.changesLookbackMaxHours, INVESTIGATION_LIMITS.changesLookbackDefaultHours),
    afterMinutes: windowShape.afterMinutes,
})
    .strict();
/** GET /alerts/:id/related */
export const RELATED_SCOPES = ['resource', 'neighbours'];
export const investigationRelatedQuerySchema = z
    .object({
    scope: z.enum(RELATED_SCOPES).default('neighbours'),
    ...windowShape,
    limit: boundedInt(1, INVESTIGATION_LIMITS.relatedMax, INVESTIGATION_LIMITS.relatedDefault),
})
    .strict();
/** GET /alerts/:id/metrics */
export const investigationMetricsQuerySchema = z
    .object({
    ...windowShape,
    /** Include the alert sensor's other datapoints on the same instance. */
    siblings: z.enum(['true', 'false']).default('true').transform((v) => v === 'true'),
})
    .strict();
/** One related alert: a summary, never the full document (no delivery
 *  or escalation internals). `startOffsetSeconds` is relative to the
 *  investigated alert's startedAt (negative = fired BEFORE it). */
export const relatedAlertSummarySchema = z
    .object({
    id: idSchema,
    relation: z.enum(['same_resource', 'neighbour']),
    resourceId: idSchema,
    resourceName: z.string(),
    sensorKey: z.string(),
    datapoint: z.string(),
    instanceName: z.string().nullable(),
    severity: z.enum(ALERT_SEVERITIES),
    peakSeverity: z.enum(ALERT_SEVERITIES),
    status: z.enum(ALERT_STATUSES),
    isNoData: z.boolean(),
    lastValue: z.number().nullable(),
    startedAt: isoDateTimeSchema,
    lastOccurredAt: isoDateTimeSchema,
    resolvedAt: isoDateTimeSchema.nullable(),
    resolveReason: z.enum(ALERT_RESOLVE_REASONS).nullable(),
    occurrenceCount: z.number().int(),
    suppressedBySdtId: idSchema.nullable(),
    suppressedByScopeId: idSchema.nullable(),
    startOffsetSeconds: z.number().int(),
})
    .strict();
/** SDT window covering the alert's target, with whether it was active
 *  when the alert started (the usual "why was this quiet" answer). */
export const sdtSummarySchema = z
    .object({
    id: idSchema,
    scopeType: z.enum(SDT_SCOPE_TYPES),
    targetId: idSchema.nullable(),
    comment: z.string().nullable(),
    startsAt: isoDateTimeSchema.nullable(),
    endsAt: isoDateTimeSchema.nullable(),
    recurring: z.boolean(),
    activeAtAlertStart: z.boolean(),
})
    .strict();
/** One audited change to the alert's resource or sensor. Only the
 *  fields that actually changed are returned; secret-looking fields are
 *  redacted and oversized values truncated. */
export const changeEntrySchema = z
    .object({
    at: isoDateTimeSchema,
    entityType: z.enum(['resource', 'sensor']),
    entityId: idSchema,
    action: z.string(),
    actor: z
        .object({
        userId: z.string(),
        displayName: z.string(),
        authSource: z.string(),
    })
        .strict(),
    changedFields: z.array(z.object({ field: z.string(), before: z.unknown(), after: z.unknown() }).strict()),
})
    .strict();
//# sourceMappingURL=investigation.schema.js.map